import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Subject, throwError } from 'rxjs';
import { LogService } from "./log.service";
import { ErrorHandlerService } from "./error-handler.service";
import { catchError, tap } from 'rxjs/operators';
import { CommandService, api } from './command.service';
import { User, Authentication } from './user.model';
import { Cookie } from './cookie';
import { LookupValueDisplayMode, SettingCategory, SettingCategoryName } from "./setting.model";
import { ArcAppAccessTypes, Application, Archive } from './shared/application.model';
import { File360Component } from './component.model';

@Injectable({
  providedIn: 'root',
})
export class UserService {

  currentUser: User = new User();
  // store the URL so we can redirect after logging in
  redirectUrl: string = "";
  // last error message
  lastErrorMessage: string = "";

  isLoggedOnChanged$: Subject<boolean> = new Subject<boolean>();

  //help page URL
  helpUrl: string = "";

  /** 
   * Emits when certain components have ngInited.
   * Used by mobile header to update the header title
   */
  componentInit$: Subject<File360Component> = new Subject<File360Component>();


  constructor(private http: HttpClient,
    private error: ErrorHandlerService,
    private log: LogService,
    private command: CommandService) { }

  getUser(): User {
    if (this.currentUser.UniqueId == null ||
      this.currentUser.UniqueId == "" ||
      this.currentUser.UniqueId.length == 0) {
      //
      // Caller is requesting the user but the object doesn't exist.
      // Try to restore it from a previous session.
      //
      this.restoreUser();
    }

    return this.currentUser;
  }

  get isLoggedOn(): boolean {
    return this.getUser().UniqueId.length > 0;
  }

  /**
   *  Get user setting based on the category and name
   * @param category Setting category
   * @param name Setting name
   */
  private getSetting(category: string, name: string): any {
    let val: any = null;

    this.currentUser.Settings.find(x => {
      if ((x.Category == category) && (x.Name == name)) {
        //found setting return the value
        return val = x.Value;
      }
    });

    //Didn't find the user setting use default
    //TO DO add defaults as needed

    if (val == null) {
      switch (category) {
        case SettingCategory.SettingCategoryHitlist:

          switch (name) {
            case SettingCategoryName.SettingCategoryNameDisplayMode:
              val = LookupValueDisplayMode.Short; //default short
              break;
          }
          break;
      }
    }
    return val;
  }

  /**
   * Returns the lookup display setting for the logged on user.
   */
  lookupDisplaySetting(): LookupValueDisplayMode {
    return this.getSetting(SettingCategory.SettingCategoryHitlist, SettingCategoryName.SettingCategoryNameDisplayMode) as LookupValueDisplayMode
  }

  private forgetUser(): void {
    Cookie.remove("uniqueId");
    Cookie.remove("userName");
    Cookie.remove("firstName");
    Cookie.remove("lastName");
    Cookie.remove("userId");
    Cookie.remove("permissions");
    Cookie.remove("settings");
    Cookie.remove("isrecordsmanager");
    Cookie.remove("webvieweroption");
    Cookie.remove("warnsystemkeytoexpiremessage");
    Cookie.remove("isdemo");
    Cookie.remove("showhitlistindicators");

    this.currentUser = new User();
    this.log.forgetUser();
  }

  private restoreUser() {
    if (Cookie.exists("uniqueId")) {
      this.log.logMessage(`restoreUser(); Trying to restore user session`);
      this.lastErrorMessage = "";
      //
      // No user object, if a cookie exists. Restore the session.
      //
      this.currentUser = new User();
      this.currentUser.UniqueId = Cookie.read("uniqueId") as string;
      this.currentUser.Name = Cookie.read("userName") as string;
      this.currentUser.FirstName = Cookie.read("firstName") as string;
      this.currentUser.LastName = Cookie.read("lastName") as string;
      this.currentUser.Id = this.currentUser.getIdFromString(Cookie.read("userId"));
      this.currentUser.WarnSystemKeyToExpireMessage = Cookie.read("warnsystemkeytoexpiremessage") as string;
      if (Cookie.exists("permissions") && Cookie.read("permissions") != "") {
        this.currentUser.Permissions = this.currentUser.getPermissionsFromString(Cookie.read("permissions"));
      }
      if (Cookie.exists("settings") && Cookie.read("settings") != "") {
        this.currentUser.Settings = this.currentUser.getSettingsFromString(Cookie.read("settings"));
      }
      if (Cookie.exists("isrecordsmanager") && Cookie.read("isrecordsmanager") != "") {
        this.currentUser.IsRecordsManager = this.currentUser.getIsRecordsManager(Cookie.read("isrecordsmanager"));
      }
      if (Cookie.exists("isadministrator") && Cookie.read("isadministrator") != "") {
        this.currentUser.IsAdministrator = this.currentUser.getIsAdministrator(Cookie.read("isadministrator"));
      }
      if (Cookie.exists("webvieweroption") && Cookie.read("webvieweroption") != "") {
        this.currentUser.WebViewerOption = Cookie.read("webvieweroption") as string;
      }
      if (Cookie.exists("isdemo") && Cookie.read("isdemo") != "") {
        this.currentUser.IsDemo = this.currentUser.getBooleanFromString(Cookie.read("isdemo"));
      }
      if (Cookie.exists("showhitlistindicators") && Cookie.read("showhitlistindicators") != "") {
        this.currentUser.showHitListIndicators = this.currentUser.getBooleanFromString(Cookie.read("showhitlistindicators"));
      }

      this.log.setUser(this.currentUser);
      this.printUser();
    }
  }

  logOn(name: string, password: string, culture: string) {

    this.log.logMessage(`logOn(); Logging on as ${name}`);
    this.lastErrorMessage = "";
    // The last attempted user name is stored in the model prior to authentication.
    this.currentUser.Name = name;

    let iauth: Authentication = {
      name: name,
      password: password,
      culture: culture,
      key: '82B8DAA9-1E56-4773-962F-92F52FF0DF6A'
    };

    //
    // Define the http get request. It will not execute until .subscribe() is called.
    //

    return this.http.post<User>(
      // the URL
      this.command.getUrl(api.Auth),
      // body
      iauth,
      // the parameters  
      this.command.logOnOptions())
      // pipe the observable result from 
      // http.get() through an RxJS catchError() operator
      .pipe(
        // tap into the flow of the observable values
        // this allows us to look at the observable values
        // and do something (e.g. log them).
        tap(user => {
          this.currentUser = new User(user);
          this.log.setUser(this.currentUser);
          this.printUser();
          //
          // Write required cookie values for this user.
          //
          Cookie.write("userId", this.currentUser.Id.toString());
          Cookie.write("userName", this.currentUser.Name);
          Cookie.write("firstName", this.currentUser.FirstName);
          Cookie.write("lastName", this.currentUser.LastName);
          Cookie.write("uniqueId", this.currentUser.UniqueId);
          Cookie.write("permissions", JSON.stringify(this.currentUser.Permissions));
          Cookie.write("settings", JSON.stringify(this.currentUser.Settings));
          Cookie.write("isrecordsmanager", JSON.stringify(this.currentUser.IsRecordsManager));
          Cookie.write("isadministrator", JSON.stringify(this.currentUser.IsAdministrator));
          Cookie.write("webvieweroption", JSON.stringify(this.currentUser.WebViewerOption));
          Cookie.write("warnsystemkeytoexpiremessage", this.currentUser.WarnSystemKeyToExpireMessage ? this.currentUser.WarnSystemKeyToExpireMessage : "");
          Cookie.write("isdemo", JSON.stringify(this.currentUser.IsDemo));


          this.currentUser.showHitListIndicators = true;

          this.isLoggedOnChanged$.next(true);
        }),
        catchError(err => { return this.error.handleError(err); })
      );
  }

  logOff() {
    if (!this.currentUser || !this.isLoggedOn) {
      //
      // Delete client cookie
      //
      this.forgetUser();
      return;
    }

    const name: string = this.currentUser.Name!;
    const log: LogService = this.log;
    this.log.logMessage(`logOff(); Logging off as ${name}`);

    //
    // Call server logoff
    //
    this.http.delete(this.command.getUrl(api.Auth), this.command.logOffOptions(this.currentUser.UniqueId!)).pipe(
      catchError(err => { return this.error.handleError(err); })
    ).subscribe(
      function (response) {
        log.logMessage(`logOff(); Logged off as ${name}`);
      }
    );

    //
    // Delete client cookie
    //
    this.forgetUser();
    this.isLoggedOnChanged$.next(false);
  }

  private printUser() {
    if (this.currentUser != null && this.currentUser.Id > 0) {
      this.log.logMessage(`printUser(); Id: ${this.currentUser.Id}
      Name: ${this.currentUser.fullName}
      Permissions: ${this.currentUser.Permissions}`);
      console.log(this.currentUser);
    }
  }

  /**
   * Get an observable that will return a list of applications accessible to the user
   * for the given access type.
   * 
   * @param accessType The access type for which to obtain a list of applications accessible to the user.
   */
  getApplications(accessType: ArcAppAccessTypes) {

    this.log.logMessage(`getApplications(); accessType is ${accessType}`);

    //
    // Define the http get request. It will not execute until .subscribe() is called.
    //

    return this.http.get<{ Key: Archive, Value: Application[] }[]>(
      // the URL
      this.command.getUrl(api.Applications),
      // the parameters  
      this.command.getApplicationsOptions(this.getUser().UniqueId, accessType))
      // pipe the observable result from 
      // http.get() through an RxJS catchError() operator
      .pipe(
        // tap into the flow of the observable values
        // this allows us to look at the observable values
        // and do something (e.g. log them).
        tap(result => {
          result.forEach(r => {
            this.log.logMessage(`getApplications(); archive ID ${r.Key.Id}; name ${r.Key.Name}`)
            r.Value.forEach(app => this.log.logMessage(`getApplications(); application ID ${app.Id}; name ${app.Name}`));
          });
        }),
        catchError(err => {
          return throwError(err);
        })
      );
  }
}
